import React, { useState, useEffect, useRef } from 'react';

export default function BejoAttendixLanding() {
  const [scrolled, setScrolled] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);
  const [animatedStats, setAnimatedStats] = useState({});
  const featureRefs = useRef([]);
  const statsRef = useRef(null);

  // Handle scroll for header background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for feature cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = featureRefs.current.indexOf(entry.target);
            setTimeout(() => {
              setVisibleCards((prev) => [...prev, index]);
            }, index * 100);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    featureRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Animated counter for stats
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateStats();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const animateStats = () => {
    const stats = [
      { key: 'uptime', target: 99.9, format: (v) => `${v.toFixed(1)}%` },
      { key: 'users', target: 10000, format: (v) => `${Math.floor(v / 1000)}k+` },
      { key: 'orgs', target: 500, format: (v) => `${Math.floor(v)}+` },
      { key: 'rating', target: 4.9, format: (v) => `${v.toFixed(1)}★` },
    ];

    stats.forEach((stat) => {
      let current = 0;
      const increment = stat.target / (2000 / 16);
      const timer = setInterval(() => {
        current += increment;
        if (current >= stat.target) {
          current = stat.target;
          clearInterval(timer);
        }
        setAnimatedStats((prev) => ({
          ...prev,
          [stat.key]: stat.format(current),
        }));
      }, 16);
    });
  };

  const features = [
    { icon: '⚡', title: 'Lightning Fast', desc: 'Record attendance in seconds with our intuitive interface. No more lengthy manual processes or paperwork.' },
    { icon: '📊', title: 'Real-Time Analytics', desc: 'Get instant insights into attendance patterns, trends, and reports at your fingertips.' },
    { icon: '🔒', title: 'Secure & Private', desc: 'Your data is encrypted and protected with enterprise-grade security standards.' },
    { icon: '📱', title: 'Mobile First', desc: 'Designed for mobile devices. Access your attendance data anywhere, anytime.' },
    { icon: '🌐', title: 'Cloud Sync', desc: 'Automatic cloud synchronization ensures your data is always up-to-date and accessible.' },
    { icon: '💼', title: 'Team Ready', desc: 'Perfect for organizations of all sizes. Scale effortlessly as your team grows.' },
  ];

  return (
    <div style={styles.container}>
      <style>{css}</style>
      
      {/* Background Effects */}
      <div style={styles.bgGradient1}></div>
      <div style={styles.bgGradient2}></div>

      {/* Header */}
      <header style={{...styles.header, ...(scrolled ? styles.headerScrolled : {})}}>
        <div style={styles.maxWidth}>
          <nav style={styles.nav}>
            <div style={styles.logo}>BejoAttendix</div>
            <a href="#download" style={styles.navCta}>Get Started</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.maxWidth}>
          <div style={styles.heroBadge}>✨ Smart Attendance Made Simple</div>
          <h1 style={styles.h1}>Track Attendance<br />with Precision</h1>
          <p style={styles.heroText}>
            The modern way to manage attendance. Fast, reliable, and designed for teams who value their time.
          </p>
          <div style={styles.ctaGroup}>
            <a href="#download" style={styles.btnPrimary}>
              Download Now
              <span style={styles.arrow}>→</span>
            </a>
            <a href="#features" style={styles.btnSecondary}>Learn More</a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={styles.features} id="features">
        <div style={styles.maxWidth}>
          <div style={styles.sectionTitle}>
            <h2 style={styles.h2}>Everything You Need</h2>
            <p style={styles.sectionText}>Powerful features designed for seamless attendance management</p>
          </div>
          <div style={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div
                key={index}
                ref={(el) => (featureRefs.current[index] = el)}
                style={{
                  ...styles.featureCard,
                  ...(visibleCards.includes(index) ? styles.featureCardVisible : {}),
                }}
              >
                <div style={styles.featureIcon}>{feature.icon}</div>
                <h3 style={styles.h3}>{feature.title}</h3>
                <p style={styles.featureText}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection} id="download">
        <div style={styles.maxWidth}>
          <h2 style={styles.ctaTitle}>Ready to Get Started?</h2>
          <p style={styles.ctaText}>
            Join thousands of teams already using BejoAttendix to streamline their attendance tracking.
          </p>
          <div style={styles.ctaGroup}>
            <a href="#" style={styles.btnPrimaryLarge}>Download for iOS</a>
            <a href="#" style={styles.btnPrimaryLarge}>Download for Android</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.maxWidth}>
          <div style={styles.footerContent}>
            <div style={styles.logo}>BejoAttendix</div>
            <div style={styles.footerLinks}>
              <a href="#" style={styles.footerLink}>Privacy</a>
              <a href="#" style={styles.footerLink}>Terms</a>
              <a href="#" style={styles.footerLink}>Support</a>
              <a href="#" style={styles.footerLink}>Contact</a>
            </div>
            <p style={styles.copyright}>© 2026 BejoAttendix. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: 'DM Sans', sans-serif;
    overflow-x: hidden;
  }

  @keyframes float {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    50% { transform: translate(-50px, 50px) rotate(5deg); }
  }
`;

const styles = {
  container: {
    position: 'relative',
    background: '#F8FAFB',
    color: '#1A2332',
    minHeight: '100vh',
  },
  bgGradient1: {
    position: 'fixed',
    top: '-50%',
    right: '-20%',
    width: '80%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(0, 102, 255, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
    animation: 'float 20s ease-in-out infinite',
    zIndex: 0,
  },
  bgGradient2: {
    position: 'fixed',
    bottom: '-30%',
    left: '-10%',
    width: '60%',
    height: '80%',
    background: 'radial-gradient(circle, rgba(0, 184, 217, 0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
    animation: 'float 25s ease-in-out infinite reverse',
    zIndex: 0,
  },
  maxWidth: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    position: 'relative',
    zIndex: 1,
  },
  header: {
    padding: '2rem 0',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    transition: 'all 0.3s ease',
  },
  headerScrolled: {
    background: 'rgba(248, 250, 251, 0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 20px rgba(0, 0, 0, 0.05)',
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '1.75rem',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #0066FF, #00B8D9)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.02em',
  },
  navCta: {
    background: '#0066FF',
    color: 'white',
    padding: '0.75rem 1.75rem',
    borderRadius: '50px',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    border: '2px solid transparent',
    fontSize: '0.95rem',
  },
  hero: {
    padding: '5rem 0 8rem',
    textAlign: 'center',
    position: 'relative',
  },
  heroBadge: {
    display: 'inline-block',
    background: '#EBF4FF',
    border: '1px solid rgba(0, 102, 255, 0.2)',
    color: '#0066FF',
    padding: '0.5rem 1.25rem',
    borderRadius: '50px',
    fontSize: '0.85rem',
    fontWeight: '600',
    marginBottom: '2rem',
  },
  h1: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '4.5rem',
    fontWeight: '800',
    lineHeight: '1.1',
    marginBottom: '1.5rem',
    background: 'linear-gradient(135deg, #1A2332 0%, #0066FF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.03em',
  },
  heroText: {
    fontSize: '1.35rem',
    color: '#5A6C7D',
    maxWidth: '600px',
    margin: '0 auto 3rem',
  },
  ctaGroup: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    background: '#0066FF',
    color: 'white',
    padding: '1rem 2.5rem',
    borderRadius: '50px',
    textDecoration: 'none',
    fontWeight: '700',
    fontSize: '1.1rem',
    transition: 'all 0.3s ease',
    border: '2px solid #0066FF',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  btnSecondary: {
    background: '#FFFFFF',
    color: '#1A2332',
    padding: '1rem 2.5rem',
    borderRadius: '50px',
    textDecoration: 'none',
    fontWeight: '700',
    fontSize: '1.1rem',
    transition: 'all 0.3s ease',
    border: '2px solid #E1E8ED',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  arrow: {
    display: 'inline-block',
    transition: 'transform 0.3s ease',
  },
  features: {
    padding: '6rem 0',
    position: 'relative',
  },
  sectionTitle: {
    textAlign: 'center',
    marginBottom: '4rem',
  },
  h2: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '2.75rem',
    fontWeight: '800',
    marginBottom: '1rem',
    letterSpacing: '-0.02em',
  },
  sectionText: {
    fontSize: '1.2rem',
    color: '#5A6C7D',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    marginTop: '3rem',
  },
  featureCard: {
    background: '#FFFFFF',
    border: '1px solid #E1E8ED',
    borderRadius: '24px',
    padding: '2.5rem',
    transition: 'all 0.4s ease',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
    opacity: 0,
    transform: 'translateY(30px)',
  },
  featureCardVisible: {
    opacity: 1,
    transform: 'translateY(0)',
  },
  featureIcon: {
    width: '60px',
    height: '60px',
    background: 'linear-gradient(135deg, #0066FF, #00B8D9)',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.75rem',
    marginBottom: '1.5rem',
  },
  h3: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '1.5rem',
    marginBottom: '1rem',
    fontWeight: '700',
  },
  featureText: {
    color: '#5A6C7D',
    lineHeight: '1.7',
  },
  stats: {
    padding: '5rem 0',
    textAlign: 'center',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '3rem',
    marginTop: '3rem',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statNumber: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '3.5rem',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #0066FF, #00B8D9)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '0.5rem',
  },
  statLabel: {
    color: '#5A6C7D',
    fontSize: '1.1rem',
  },
  ctaSection: {
    padding: '4rem 0',
    textAlign: 'center',
  },
  ctaTitle: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '3rem',
    fontWeight: '800',
    marginBottom: '1.5rem',
  },
  ctaText: {
    fontSize: '1.2rem',
    color: '#5A6C7D',
    maxWidth: '600px',
    margin: '0 auto 2.5rem',
  },
  btnPrimaryLarge: {
    background: '#0066FF',
    color: 'white',
    padding: '1.25rem 3rem',
    borderRadius: '50px',
    textDecoration: 'none',
    fontWeight: '700',
    fontSize: '1.1rem',
    transition: 'all 0.3s ease',
    border: '2px solid #0066FF',
  },
  footer: {
    padding: '3rem 0',
    borderTop: '1px solid #E1E8ED',
    marginTop: '5rem',
  },
  footerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '2rem',
  },
  footerLinks: {
    display: 'flex',
    gap: '2rem',
  },
  footerLink: {
    color: '#5A6C7D',
    textDecoration: 'none',
    transition: 'color 0.3s ease',
  },
  copyright: {
    color: '#5A6C7D',
    fontSize: '0.9rem',
  },
};
